import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, reelThumbnail } from "../lib/api";
import { Play, X, ArrowRight } from "@phosphor-icons/react";
import { StreamHLSPlayer } from "../components/CMSPanel";

const FILTERS = ["All", "Computer Vision", "LLM Copilot", "Voice Interfaces", "Dashboards", "Optimization", "Automation"];

export default function ReelLibrary() {
  const [reels, setReels] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/reels").then(({ data }) => setReels(data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? reels : reels.filter((r) => r.category === filter)),
    [reels, filter]
  );

  return (
    <div data-testid="reel-library-page" className="px-6 md:px-14 py-16 md:py-24">
      <div className="max-w-6xl">
        <div className="text-overline mb-4">◢ Reel library</div>
        <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]">
          Show, don't <span className="text-[#FF4500]">tell</span>.
        </h1>
        <p className="mt-5 text-[#8E8E93] max-w-2xl text-[15px]">
          10–20 second clips. Each one solves one thing — and tells you what.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="reel-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
            className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
              filter === f
                ? "border-[#FF4500] text-white bg-[#FF4500]/10"
                : "border-[#2A2A2A] text-[#8E8E93] hover:text-white hover:border-[#404040]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
        {filtered.map((r) => (
          <button
            key={r.id}
            data-testid={`reel-card-${r.id}`}
            onClick={() => setOpen(r)}
            className="text-left bg-[#050505] aspect-[16/10] relative overflow-hidden group ww-trace"
          >
            <img src={reelThumbnail(r)} alt={r.title} className="absolute inset-0 w-full h-full object-cover opacity-65 group-hover:opacity-85 group-hover:scale-105 transition-all duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{r.category}</span>
              <span className="font-mono text-[10px] text-[#8E8E93]">{r.duration_sec}s</span>
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              <div className="font-display text-lg text-white">{r.title}</div>
              <div className="mt-1 text-[#8E8E93] text-xs line-clamp-2">{r.what_it_solves}</div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-14 h-14 rounded-full ww-glass flex items-center justify-center border border-[#FF4500]">
                <Play size={20} color="#FF4500" weight="fill" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {open && (
        <div data-testid="reel-modal" className="fixed inset-0 z-50 bg-[#050505]/95 backdrop-blur flex items-center justify-center p-6" onClick={() => setOpen(null)}>
          <div className="max-w-3xl w-full bg-[#0a0a0a] border border-[#2A2A2A] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Media surface: letterboxes portrait AND landscape videos, no cropping.
                Height is capped at ~65vh so tall portrait clips still leave room for the caption card. */}
            <div className="relative bg-[#050505] flex items-center justify-center max-h-[65vh]">
              {open.video_url ? (
                (/\.m3u8(\?|#|$)/i.test(open.video_url) || /cloudflarestream\.com/i.test(open.video_url)) ? (
                  <div className="w-full max-h-[65vh]" data-testid="reel-stream-player">
                    <StreamHLSPlayer src={open.video_url} autoplay={true} poster={reelThumbnail(open)} />
                  </div>
                ) : (
                  <video
                    src={mediaUrl(open.video_url)}
                    poster={reelThumbnail(open)}
                    controls
                    autoPlay
                    playsInline
                    className="max-h-[65vh] max-w-full w-auto h-auto object-contain"
                    data-testid="reel-video"
                  />
                )
              ) : (
                <img src={reelThumbnail(open)} alt={open.title} className="max-h-[65vh] max-w-full w-auto h-auto object-contain" />
              )}
              <button onClick={() => setOpen(null)} className="absolute top-4 right-4 text-white p-2 z-10 bg-[#050505]/70 hover:bg-[#050505]/90 transition-colors"><X size={20} /></button>
            </div>
            <div className="p-6 md:p-8">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{open.category}</div>
              <h3 className="mt-2 font-display text-3xl text-white">{open.title}</h3>
              <p className="mt-3 text-[#D7D7D9]">{open.what_it_solves}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {open.tech?.map((t) => <span key={t} className="ww-chip">{t}</span>)}
              </div>
              {!open.video_url && (
                <div className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-[#555]">
                  Reel preview · video upload pending
                </div>
              )}
              <Link
                to={`/reels/${open.slug || open.id}`}
                data-testid="reel-open-detail"
                className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00] hover:text-white transition-colors"
              >
                Open full detail page <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
